import { INVOICE_LINE_TYPES, INCLUDE_SERVICE_LINES_KEY, SERVICE_LINE_BUNDLE_KEYS } from '@/utils/constants';

/**
 * Decide which service-line bundle keys must be appended when a user turns on
 * "Include service lines" (key {@link INCLUDE_SERVICE_LINES_KEY}).
 *
 * Selecting "Include service lines" is a single toggle that also pulls in trip
 * and run service lines ({@link SERVICE_LINE_BUNDLE_KEYS}). This is a pure
 * function so the cascade decision can be unit tested independently of the Vue
 * component and its watcher timing.
 *
 * The cascade only fires on a genuine transition of "Include service lines"
 * from not-selected to selected. It deliberately does NOT fire when the value
 * merely loads with service lines already present (old value empty), so that
 * loading a stored configuration such as [2, 4, 64] is not mistaken for a fresh
 * user toggle and does not silently append 128 (run service lines).
 *
 * @param {Array<{key: number}>|null} newValue Currently selected invoice line types.
 * @param {Array<{key: number}>|null} oldValue Previously selected invoice line types.
 * @returns {Array<{key: number}>} The bundle line types missing from newValue that
 *   should be appended, or an empty array when no cascade is required.
 */
export function resolveInvoiceLineBundleAdditions(newValue, oldValue) {
    const current = newValue || [];
    const previous = oldValue || [];

    const includedNow = current.some((x) => x.key === INCLUDE_SERVICE_LINES_KEY);
    const includedBefore = previous.some((x) => x.key === INCLUDE_SERVICE_LINES_KEY);

    if (!includedNow || includedBefore) {
        return [];
    }

    return INVOICE_LINE_TYPES.filter(
        (t) => SERVICE_LINE_BUNDLE_KEYS.includes(t.key) && !current.some((v) => v.key === t.key)
    );
}
